import React, { useRef, useState } from 'react';
import styled, { css } from 'astroturf/react';

import { Body } from 'js/design_system/Body';

import {
  CoScientistLandingImages,
  ImageSize,
  ResearchExampleImages,
} from './types';

type Example = {
  role: string;
  quote: string;
  chartTitle: string;
  chartAlt: string;
  images: ResearchExampleImages;
  chartSize: ImageSize;
  /* The chart fills whatever height the other cards set, cropped from the bottom */
  cropChart?: boolean;
};

const Track = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 421px));
  justify-content: center;
  align-items: stretch;
  gap: var(--spacing-sm-24, 24px);
  width: 100%;
  text-align: start;

  @media (max-width: 991px) {
    display: flex;
    justify-content: flex-start;
    align-items: stretch;
    gap: var(--spacing-xs-8, 8px);
    padding: 0 var(--spacing-sm-24, 24px);
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding: 0 var(--spacing-sm-24, 24px);
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Card = styled.article<{ lowered?: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--background-beige-light, #f9f8f6);
  border-radius: var(--border-radius-radius-md, 8px);

  /* The middle card keeps its natural height; the outer cards are inset 32px top and bottom
     and stretch, so they match each other */
  @media (min-width: 992px) {
    align-self: center;

    &.lowered {
      align-self: stretch;
      margin: var(--spacing-sm-32, 32px) 0;
    }
  }

  @media (max-width: 991px) {
    flex: 0 0 min(342px, 100%);
    scroll-snap-align: center;
  }
`;

const Testimonial = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  padding: var(--spacing-sm-32, 32px) var(--spacing-sm-32, 32px)
    var(--spacing-sm-24, 24px);
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: var(--border-radius-radius-round, 104px);
  object-fit: cover;
`;

const Quote = styled.blockquote`
  @import 'stylesheets/design_system/font';

  @include sans-serif__medium;
  margin: 0;
  font-weight: var(--type-font-weight-700, 700);
`;

const Chart = styled.figure`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  margin: 0;
  padding: 0 var(--spacing-sm-32, 32px) var(--spacing-sm-24, 24px);
`;

/* Below 992px the cards become a carousel and the tallest chart is cropped to match */
const CroppedChartFrame = styled.div`
  @media (max-width: 991px) {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
`;

const chartTitle = css`
  &[class] {
    font-style: italic;
  }
`;

const ChartImage = styled.img<{ cropped?: boolean }>`
  display: block;
  width: 100%;
  height: auto;

  /* On desktop, outer-card charts fill their card's extra height, centred and uncropped.
     Zooming in (object-fit: cover) cut off the axis labels at the chart edges. */
  @media (min-width: 992px) {
    flex: 1 0 auto;
    object-fit: contain;
  }

  @media (max-width: 991px) {
    &.cropped {
      position: absolute;
      inset: 0;
      height: 100%;
      object-fit: cover;
      object-position: top;
    }
  }
`;

const Dots = styled.div`
  display: none;
  justify-content: center;
  gap: var(--spacing-xs-8, 8px);

  @media (max-width: 991px) {
    display: flex;
  }
`;

const Dot = styled.button<{ active?: boolean }>`
  width: 8px;
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: var(--border-radius-radius-round, 104px);
  background: var(--stroke-light, #dddde2);
  cursor: pointer;
  transition: width 0.2s ease;

  &.active {
    width: 24px;
    background: var(--button-primary-fill, #0645b1);
  }
`;

type ResearchExamplesProps = {
  images: CoScientistLandingImages['examples'];
};

const ResearchExamples: React.FC<ResearchExamplesProps> = ({ images }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const examples: Example[] = [
    {
      role: 'Postdoc, Cancer Biology',
      quote: '“I benchmarked our RNA-seq data against TCGA in an afternoon.”',
      chartTitle: 'Gene Expression',
      chartAlt: 'Volcano plot of gene expression fold change against TCGA',
      images: images.cancerBiology,
      chartSize: [730, 482],
    },
    {
      role: 'Professor, Labor Economics',
      quote:
        '“I built a model to compare how rent control plays out across different cities.”',
      chartTitle: 'US State Grid Map',
      chartAlt: 'Map of the United States shaded by county',
      images: images.laborEconomics,
      chartSize: [840, 620],
      cropChart: true,
    },
    {
      role: 'Grad Researcher, Physical Chemistry',
      quote: '“I analyzed 60 reactions in a day. That used to take me weeks.”',
      chartTitle: 'Reaction kinetics',
      chartAlt: 'Concentration decay curves for three rate constants',
      images: images.physicalChemistry,
      chartSize: [698, 463],
    },
  ];

  const cards = () =>
    Array.from(trackRef.current?.children ?? []) as HTMLElement[];

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;

    // Scrolling stops at the track edges before an outer card can be centered
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    if (track.scrollLeft <= 1) {
      setActiveIndex(0);
      return;
    }
    if (track.scrollLeft >= maxScrollLeft - 1) {
      setActiveIndex(cards().length - 1);
      return;
    }

    const center = track.scrollLeft + track.clientWidth / 2;
    let closest = 0;
    cards().forEach((card, i) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const closestCard = cards()[closest];
      const closestCenter =
        closestCard.offsetLeft + closestCard.offsetWidth / 2;
      if (Math.abs(cardCenter - center) < Math.abs(closestCenter - center)) {
        closest = i;
      }
    });
    setActiveIndex(closest);
  };

  const scrollToCard = (index: number) => {
    const track = trackRef.current;
    const card = cards()[index];
    if (!track || !card) return;

    track.scrollTo({
      left: card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <Track ref={trackRef} onScroll={handleScroll}>
        {examples.map((example, i) => (
          <Card key={example.role} lowered={i !== 1}>
            <Testimonial>
              <Identity>
                <Avatar src={example.images.avatar} alt="" loading="lazy" />
                <Body size="md">{example.role}</Body>
              </Identity>
              <Quote>{example.quote}</Quote>
            </Testimonial>
            <Chart>
              <Body as="figcaption" size="sm" className={chartTitle}>
                {example.chartTitle}
              </Body>
              {example.cropChart ? (
                <CroppedChartFrame>
                  <ChartImage
                    cropped
                    src={example.images.chart}
                    alt={example.chartAlt}
                    width={example.chartSize[0]}
                    height={example.chartSize[1]}
                    loading="lazy"
                  />
                </CroppedChartFrame>
              ) : (
                <ChartImage
                  src={example.images.chart}
                  alt={example.chartAlt}
                  width={example.chartSize[0]}
                  height={example.chartSize[1]}
                  loading="lazy"
                />
              )}
            </Chart>
          </Card>
        ))}
      </Track>
      <Dots>
        {examples.map((example, i) => (
          <Dot
            key={example.role}
            type="button"
            active={i === activeIndex}
            aria-label={`Show example ${i + 1} of ${examples.length}`}
            aria-current={i === activeIndex}
            onClick={() => scrollToCard(i)}
          />
        ))}
      </Dots>
    </>
  );
};

export default ResearchExamples;
